import { Component, OnInit, PLATFORM_ID, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';

import {
  AdminRecipient, AdminUserContact, MessageRuleService, RuleMatrix, RuleScopeType, RuleTypeDto,
  RULE_SCOPE_TYPES, isSingletonScope,
} from '../../services/message-rule';
import { MESSAGE_CHANNELS } from '../../services/communication-config';
import { ToastService } from '../../services/toast';

/** A row flattened for p-select: `label` is what the filter searches on. */
interface ScopeOption {
  scopeId: number;
  label: string;
  overriddenTypes: number;
}

/**
 * Which channels each message type is delivered on, per scope.
 *
 * The matrix is saved as a UNIT (one Save button, one POST) rather than per-cell autosave: a
 * half-applied matrix would leave an owner in a state nobody chose, and channel changes cost real
 * money once they take effect.
 */
@Component({
  selector: 'app-messaging-rules',
  standalone: true,
  imports: [
    CommonModule, FormsModule, TableModule, ButtonModule, SelectModule, InputTextModule,
    IconFieldModule, InputIconModule, ToggleSwitchModule, TooltipModule, TagModule, DialogModule,
  ],
  templateUrl: './messaging-rules.html',
  styleUrls: ['./messaging-rules.css'],
})
export class MessagingRulesTab implements OnInit {
  private svc = inject(MessageRuleService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);

  readonly scopeTypes = RULE_SCOPE_TYPES;

  /**
   * When set, the picker is hidden and this component edits exactly one owner's rules. Used by
   * the client/partner detail pages so an admin can configure messaging without leaving the
   * record they are looking at.
   */
  lockedScopeType = input<RuleScopeType | null>(null);
  lockedScopeId = input<number | null>(null);

  scopeType = signal<RuleScopeType>('GLOBAL');
  scopes = signal<ScopeOption[]>([]);
  scopeId = signal<number | null>(null);
  loadingScopes = signal(false);
  /** Surfaced in the UI — a failed lookup must not look like "there are none". */
  scopeError = signal<string | null>(null);

  matrix = signal<RuleMatrix | null>(null);
  loading = signal(false);
  saving = signal(false);
  dirty = signal(false);

  // "Global" here means "has nothing above it to inherit from", which is true of ADMIN too —
  // that is what hides the Inherit column and the owner picker for both.
  readonly isGlobal = computed(() => isSingletonScope(this.scopeType()));
  readonly isAdmin = computed(() => this.scopeType() === 'ADMIN');
  readonly isEmbedded = computed(() => this.lockedScopeType() != null);
  readonly channels = computed(() => this.matrix()?.channels ?? []);

  // ---- admin distribution list ----
  recipients = signal<AdminRecipient[]>([]);
  recipientsDirty = signal(false);
  savingRecipients = signal(false);

  constructor() {
    // Embedded mode: follow the bound owner. untracked() around the body so the effect
    // depends only on the inputs, not on everything loadMatrix() happens to read.
    effect(() => {
      const t = this.lockedScopeType();
      const id = this.lockedScopeId();
      if (!t || !isPlatformBrowser(this.platformId)) return;
      untracked(() => {
        this.scopeType.set(t);
        this.scopeId.set(id ?? 0);
        this.loadMatrix();
      });
    });
  }

  /** Channels enabled somewhere in this matrix but with no provider configured. */
  readonly missingProviders = computed(() => {
    const m = this.matrix();
    if (!m) return [];
    return m.channels.filter(
      (c) => !m.providerConfigured[c] && m.types.some((t) => !t.isLocked && t.channels[c]),
    );
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    // Embedded mode loads from the effect above, driven by the bound owner.
    if (!this.isEmbedded()) this.loadMatrix();
  }

  // ---------------------------------------------------------------- scope picker ----

  /** Placeholder/empty text that distinguishes "still loading" and "lookup failed"
   *  from "there genuinely are none" — the previous UI showed "No matches" for all three. */
  readonly scopesPlaceholder = computed(() => {
    if (this.loadingScopes()) return 'Loading…';
    if (this.scopeError()) return 'Could not load — see message below';
    const kind = this.scopeType() === 'PARTNER' ? 'partner' : 'client';
    return this.scopes().length ? `Select a ${kind}` : `No ${kind}s found`;
  });

  readonly scopesEmptyMessage = computed(() =>
    this.scopeError() ? 'Lookup failed.' : 'No matches.',
  );

  onScopeTypeChange(t: RuleScopeType) {
    this.scopeType.set(t);
    this.scopeId.set(null);
    this.scopes.set([]);
    this.scopeError.set(null);
    this.matrix.set(null);
    this.dirty.set(false);

    if (isSingletonScope(t)) {
      this.loadMatrix();
      if (t === 'ADMIN') this.loadRecipients();
    } else {
      this.loadScopes();
    }
  }

  // ---------------------------------------------------------------- recipients ------

  private loadRecipients() {
    this.svc.getRecipients().subscribe({
      next: (res: any) => {
        this.recipients.set(res?.data ?? []);
        this.recipientsDirty.set(false);
      },
      error: () => this.recipients.set([]),
    });
  }

  toggleRecipient(userId: number, selected: boolean) {
    this.recipients.update((l) => l.map((r) => (r.userId === userId ? { ...r, isSelected: selected } : r)));
    this.recipientsDirty.set(true);
  }

  /** Which channels an admin can actually be reached on, given the contacts on their account.
   *  Null-tolerant: this is also read from the dialog header, which renders before an admin
   *  has been picked — a throw there aborts the whole view, blanking the table behind it. */
  contactFor(r: AdminRecipient | null | undefined): string {
    if (!r) return '';
    const bits: string[] = [];
    if (r.email) bits.push(r.email);
    if (r.mobile) bits.push(r.mobile);
    return bits.length ? bits.join(' · ') : 'no email or mobile on file';
  }

  readonly selectedCount = computed(() => this.recipients().filter((r) => r.isSelected).length);

  // ---- extra contacts for one admin (a second phone, a shared ops number) ----
  readonly channelOptions = MESSAGE_CHANNELS;
  contactsFor = signal<AdminRecipient | null>(null);
  contacts = signal<AdminUserContact[]>([]);
  savingContacts = signal(false);

  openContacts(r: AdminRecipient) {
    this.contactsFor.set(r);
    this.contacts.set([]);
    this.svc.getUserContacts(r.userId).subscribe({
      next: (res: any) => this.contacts.set(res?.data ?? []),
      error: () => this.contacts.set([]),
    });
  }

  closeContacts() {
    this.contactsFor.set(null);
  }

  addContact() {
    const u = this.contactsFor();
    if (!u) return;
    this.contacts.update((l) => [
      ...l,
      { userId: u.userId, channel: 'SMS', contact: '', label: null, isActive: true },
    ]);
  }

  patchContact(i: number, patch: Partial<AdminUserContact>) {
    this.contacts.update((l) => l.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  }

  removeContact(i: number) {
    this.contacts.update((l) => l.filter((_, idx) => idx !== i));
  }

  saveContacts() {
    const u = this.contactsFor();
    if (!u) return;

    // Blank rows are unfinished edits, and the API rejects them — dropping them here stops one
    // half-typed row failing the whole save.
    const list = this.contacts().filter((c) => (c.contact ?? '').trim().length > 0);

    this.savingContacts.set(true);
    this.svc.saveUserContacts(u.userId, list).subscribe({
      next: (res: any) => {
        this.savingContacts.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not save the contacts.');
          return;
        }
        this.toast.success(`Saved ${list.length} extra contact(s) for ${u.username}.`);
        this.closeContacts();
      },
      error: () => this.savingContacts.set(false),
    });
  }

  saveRecipients() {
    const ids = this.recipients().filter((r) => r.isSelected).map((r) => r.userId);

    this.savingRecipients.set(true);
    this.svc.saveRecipients(ids).subscribe({
      next: (res: any) => {
        this.savingRecipients.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not save the recipient list.');
          return;
        }
        this.recipients.set(res?.data ?? this.recipients());
        this.recipientsDirty.set(false);
        this.toast.success(ids.length ? `${ids.length} admin(s) will receive alerts.` : 'No admins will receive alerts.');
      },
      error: () => this.savingRecipients.set(false),
    });
  }

  /** Loads every owner once; the dropdown filters client-side, so there is no
   *  debounced round trip per keystroke. */
  private loadScopes() {
    const t = this.scopeType();
    if (t === 'GLOBAL') return;

    this.loadingScopes.set(true);
    this.scopeError.set(null);
    this.svc.getScopes(t).subscribe({
      next: (res: any) => {
        this.loadingScopes.set(false);
        if (res?.success === false) {
          this.scopeError.set(res?.message || 'Could not load the list.');
          return;
        }
        const rows = (res?.data ?? []) as any[];
        this.scopes.set(
          rows.map((r) => ({
            scopeId: r.scopeId,
            overriddenTypes: r.overriddenTypes ?? 0,
            // Code and the override count go in the label so the filter matches on both.
            label: [r.name, r.code ? `(${r.code})` : null, r.overriddenTypes ? '• custom' : '• inherits']
              .filter(Boolean)
              .join(' '),
          })),
        );
      },
      error: (e) => {
        this.loadingScopes.set(false);
        this.scopeError.set(
          e?.status === 403 || e?.status === 401
            ? 'You do not have permission to read messaging rules (Admin only).'
            : `Could not load the list${e?.status ? ' (HTTP ' + e.status + ')' : ''}.`,
        );
      },
    });
  }

  async pickScope(id: number | null) {
    if (this.dirty() && !(await this.confirmDiscard())) return;
    this.scopeId.set(id);
    if (id == null) {
      this.matrix.set(null);
      return;
    }
    this.loadMatrix();
  }

  private confirmDiscard() {
    return this.toast.confirm(
      'You have unsaved rule changes. Switching will discard them. Continue?',
      'Discard changes?',
      'Discard',
    );
  }

  // ---------------------------------------------------------------- matrix ----------

  loadMatrix() {
    const t = this.scopeType();
    // Singleton scopes (GLOBAL, ADMIN) carry no owner id — testing for one here made the
    // ADMIN matrix return before it ever called the API.
    if (!isSingletonScope(t) && !this.scopeId()) return;

    this.loading.set(true);
    this.svc.getMatrix(t, this.scopeId() ?? 0).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        this.dirty.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not load the rules.');
          return;
        }
        this.matrix.set(res?.data ?? null);
      },
      error: () => this.loading.set(false),
    });
  }

  /** Flip one cell locally. Nothing is persisted until Save. */
  toggle(typeKey: string, channel: string, value: boolean) {
    const m = this.matrix();
    if (!m) return;
    this.matrix.set({
      ...m,
      types: m.types.map((t) =>
        t.messageType === typeKey
          // Editing an inherited row makes it this scope's own — that is what the admin means
          // by touching it, and the Inherit toggle is how they hand it back.
          ? { ...t, inherited: false, channels: { ...t.channels, [channel]: value } }
          : t,
      ),
    });
    this.dirty.set(true);
  }

  /** Hand a row back to the global defaults. */
  toggleInherit(typeKey: string, inherit: boolean) {
    const m = this.matrix();
    if (!m) return;
    this.matrix.set({
      ...m,
      types: m.types.map((t) => (t.messageType === typeKey ? { ...t, inherited: inherit } : t)),
    });
    this.dirty.set(true);
  }

  rowCount(t: { channels: Record<string, boolean> }): number {
    return Object.values(t.channels ?? {}).filter(Boolean).length;
  }

  // ---------------------------------------------------------------- save ------------

  save(confirmGlobalSilence = false) {
    const m = this.matrix();
    if (!m) return;

    const types: RuleTypeDto[] = m.types
      .filter((t) => !t.isLocked) // LOGIN is never written — it always sends
      .map((t) => ({
        messageType: t.messageType,
        inherit: t.inherited,
        channels: { ...t.channels },
      }));

    this.saving.set(true);
    this.svc
      .saveMatrix({ scopeType: m.scopeType, scopeId: m.scopeId, types, confirmGlobalSilence })
      .subscribe({
        next: async (res: any) => {
          this.saving.set(false);

          // 409 = saving a GLOBAL type with no channels, which silences it for every scope that
          // inherits. Confirm explicitly rather than letting it happen quietly.
          if (res?.success === false && res?.statusCode === 409) {
            const ok = await this.toast.confirm(res.message, 'Switch off for everyone?', 'Switch off');
            if (ok) this.save(true);
            return;
          }
          if (res?.success === false) {
            this.toast.error(res?.message || 'Could not save the rules.');
            return;
          }

          this.matrix.set(res?.data ?? this.matrix());
          this.dirty.set(false);
          this.toast.success('Messaging rules saved.');
        },
        error: () => this.saving.set(false),
      });
  }

  async resetAll() {
    const m = this.matrix();
    if (!m || m.scopeType === 'GLOBAL') return;

    const ok = await this.toast.confirm(
      `${m.scopeName} will follow the global defaults for every message type.`,
      'Reset to global defaults?',
      'Reset',
    );
    if (!ok) return;

    this.svc.resetToInherit(m.scopeType, m.scopeId).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not reset.');
          return;
        }
        this.matrix.set(res?.data ?? null);
        this.dirty.set(false);
        this.toast.success('Reset to global defaults.');
      },
    });
  }
}
